import { useTranslation } from 'react-i18next';
import { Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { HighlightBox } from './HighlightBox';
import { TableRowData } from './tableData';
import { isPositiveSavings } from './utils';

export const EnvironmentalTable = ({
  data,
  colWidths,
  hasSavingsHighlighted = false,
}: {
  data: TableRowData[];
  colWidths?: number[];
  hasSavingsHighlighted?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Table
      id="environmental-table"
      sx={{
        tableLayout: 'fixed',
        width: '100%',
        ...(hasSavingsHighlighted
          ? {
              backgroundColor: 'action.hover',
              borderRadius: '6px',
              mt: 1.5,
            }
          : {}),
      }}
    >
      <colgroup>
        {colWidths?.map((width, index) => (
          <col key={index} style={{ width: `${width}%` }} />
        ))}
      </colgroup>

      <TableHead>
        <TableRow sx={{ borderBottom: (theme) => `1px solid ${theme.palette.grey[200]}` }}>
          <TableCell>
            <Typography variant="caption" fontWeight="600">
              {t('blueprints:details.table.environmentalCategory')}
            </Typography>
          </TableCell>
          {[
            'blueprints:details.table.projectWithoutBattery',
            'blueprints:details.table.greenerHybridSetup',
            'blueprints:details.table.savingsAmount',
            'blueprints:details.table.savings',
          ].map((key) => (
            <TableCell key={key}>
              <Typography variant="caption" color="grey.600">
                {t(key)}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {data.map((row, index) => {
          const hasBottomBorder = index < data.length - 1;

          return (
            <TableRow
              key={index}
              sx={{
                borderBottom: hasBottomBorder ? (theme) => `1px solid ${theme.palette.grey[200]}` : 'none',
              }}
            >
              <TableCell sx={{ border: 'none' }}>
                <Typography variant="body2" color="text.secondary">
                  {row.label}
                </Typography>
              </TableCell>

              <TableCell sx={{ border: 'none' }}>
                <Typography variant="body2">{row.baseValue}</Typography>
              </TableCell>

              <TableCell sx={{ border: 'none' }}>
                <Typography variant="body2">{row.hybridValue}</Typography>
              </TableCell>

              <TableCell sx={{ border: 'none' }}>
                <Typography
                  variant="subtitle2"
                  color={(theme) =>
                    isPositiveSavings(row.savingsAmount) ? theme.palette.success.main : theme.palette.grey[600]
                  }
                >
                  <HighlightBox
                    backgroundNegative="transparent"
                    backgroundPositive={hasSavingsHighlighted ? undefined : 'transparent'}
                  >
                    {row.savingsAmount}
                  </HighlightBox>
                </Typography>
              </TableCell>

              <TableCell sx={{ border: 'none' }}>
                <Typography
                  variant="subtitle2"
                  color={(theme) =>
                    isPositiveSavings(row.savings) ? theme.palette.success.main : theme.palette.grey[600]
                  }
                >
                  {row.savings || ''}
                </Typography>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
